import { StorageService } from './../../shared/services/storage.service';
import { NavController } from '@ionic/angular';
import { UserDialogService } from '../../profile/services/userdialog.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of, from, Observable } from 'rxjs';
import { catchError, switchMap, tap, take, filter, map, concatMap } from 'rxjs/operators';
import * as AuthActions from './auth.actions';
import { Injectable, NgZone } from '@angular/core';
import { AuthActionTypes, LoginFailAction } from './auth.actions';
import { AuthHttpService } from '../services/auth.httpservice';
import { IdAuthService } from '../services/id-auth.service';
import { selectAnonymousToken, selectRefreshToken } from './auth.selectors';
import { Geolocation } from '@capacitor/geolocation';

@Injectable()
export class AuthEffects {

	constructor(
		private actions$: Actions,
		private authEndpoint: AuthHttpService,
		private storageService: StorageService,
		private userDialogService: UserDialogService,
		private navCtrl: NavController,
		private zone: NgZone,
		private idAuth: IdAuthService,
		private store: Store
	) {

	}

	load$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.Load),
            switchMap((_: AuthActions.LoadAction) => this.authEndpoint.getCurrentUser().pipe(
                switchMap(res => of(new AuthActions.LoadOkAction(res as any))),
                catchError(res => {
                    // A stored access_token that's expired or otherwise invalid (and that the interceptor
                    // couldn't refresh) comes back as 401 - without clearing it, every relaunch keeps
                    // reloading the same dead token from storage and hitting the same 401 forever. Drop it
                    // and fall back to no session, which starts a new app-local anonymous user (ID-8).
                    if (res.status === 401) {
                        return from(this.storageService.removeStore()).pipe(
                            switchMap(() => of(new AuthActions.SignedOutAction()))
                        );
                    }
                    return of(new AuthActions.LoadFailAction(res));
                }))
            )
        )
    });

	// @Effect({ dispatch: false })
	// loadFail$ = this.actions$.pipe(
	// 	ofType(ProfileActionTypes.LoadFail),
	// 	tap((_: ProfileActions.LoadFailAction) => {
	// 		console.error(`FOUT: Laden van profiel mislukt.`);
	// 	})
	// );

	// ID-5: to Pro Pool ID's login. The web build leaves the page here and comes back through idCallbackGuard;
	// a native app gets the callback URL back from the system browser.
	login$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.Login),
            switchMap((action: AuthActions.LoginAction) => from(this.idAuth.startLogin(action.register)).pipe(
                filter(callbackUrl => !!callbackUrl),
                map(callbackUrl => new AuthActions.LoginCallbackAction(callbackUrl)),
                catchError(error => of(new AuthActions.LoginFailAction(loginError(error))))
            ))
	    );
    });

	loginCallback$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.LoginCallback),
            switchMap((action: AuthActions.LoginCallbackAction) => from(this.idAuth.completeLogin(action.url)).pipe(
                map(tokens => new AuthActions.LoginOkAction(tokens)),
                catchError(error => of(new AuthActions.LoginFailAction(loginError(error))))
            ))
	    );
    });

	// Every login gives the account its app user first. ID-8's upgrade path: from an anonymous session the Pro Pool ID
	// account takes over that anonymous user. Without one (ID-6B: registered at Pro Pool ID) the first login gets a new
	// app user. The API decides; an account that already has its own user keeps it. Sent with the new token, which the
	// interceptor prefers over the anonymous session. A failed link still loads whoever the account is.
	// Then the copy of the profile catches up with Pro Pool ID (ID-10) before it is loaded; a failed refresh still loads.
	loginOk$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.LoginOk),
            concatMap(() => this.store.select(selectAnonymousToken).pipe(take(1))),
            concatMap(anonymousToken => this.authEndpoint.linkAnonymousUser(anonymousToken ?? null).pipe(
                catchError(() => of(null))
            ).pipe(
                concatMap(() => this.authEndpoint.refreshProfile().pipe(catchError(() => of(null)))),
                concatMap(() => of(new AuthActions.AnonymousSessionEndedAction(), new AuthActions.LoadAction()))
            )),
            tap(action => {
                if (action.type === AuthActionTypes.Load) {
                    this.navCtrl.navigateRoot('/tabs/profile');
                }
            })
        )
    });

	// Wrong credentials, lockout and the like are answered on Pro Pool ID's own login screen; what lands here is a
	// login that came back unusable (wrong state, refused code exchange, Pro Pool ID unreachable).
	loginFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.LoginFail),
            tap((action: LoginFailAction) => {
                console.error('FOUT: Inloggen bij Pro Pool ID mislukt.', action.error);
                this.userDialogService.globalErrorAlert();
            })
        )
    }, { dispatch: false });

	// Drops the local session first, then revokes the refresh token and ends the Pro Pool ID session (ID-15's
	// RP-initiated logout), so the next login asks for credentials again.
	logout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.Logout),
            switchMap(() => this.store.select(selectRefreshToken).pipe(take(1))),
            switchMap(refreshToken => from(this.storageService.removeStore()).pipe(
                tap(() => { this.idAuth.endSession(refreshToken); }),
                map(() => new AuthActions.SignedOutAction())
            ))
        )
    });

	// ID-8: no session means a new app-local anonymous user, which Pro Pool ID never sees.
	signedOut$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.SignedOut),
            switchMap(() => this.authEndpoint.startAnonymousSession().pipe(
                map(res => new AuthActions.AnonymousSessionOkAction(res.anonymous_token)),
                catchError(error => of(new AuthActions.AnonymousSessionFailAction({ status: error?.status })))
            ))
        )
    });

	anonymousSessionOk$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.AnonymousSessionOk),
            map(() => new AuthActions.LoadAction())
        )
    });

	loadStorage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.LoadStorage),
            switchMap((_: AuthActions.LoadStorageAction) => {
                return from(this.storageService.getStore()).pipe(
                    take(1),
                    switchMap(result => of(new AuthActions.LoadStorageOkAction(JSON.parse(result)))),
                    catchError(result => of(new AuthActions.LoadStorageFailAction(result)))
                );
            })
        )
    });

	loadStorageOk$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.LoadStorageOk),
            filter((action: AuthActions.LoadStorageOkAction) => !hasSession(action.store)),
            map(() => new AuthActions.SignedOutAction()),
        )
    });

	loadStorageOk2$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.LoadStorageOk),
            filter((action: AuthActions.LoadStorageOkAction) => hasSession(action.store)),
            switchMap((_: AuthActions.LoadStorageOkAction) => {
                return of(new AuthActions.LoadAction());
            }),
        )
    });

	loadStorageFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.LoadStorageFail),
            tap(() => {
                console.error(`FOUT: Store laden mislukt.`);
            })
        );
     }, { dispatch: false });

	// ID-10: the profile lives at Pro Pool ID, so the app asks the API to pull the current values into its copy.
	// switchMap on purpose: a resume while an earlier refresh is still in flight wants the newer answer.
	refreshProfile$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.RefreshProfile),
            switchMap((_: AuthActions.RefreshProfileAction) => {
                return this.authEndpoint.refreshProfile().pipe(
                    switchMap(res => of(new AuthActions.RefreshProfileOkAction(res as any))),
                    catchError(err => of(new AuthActions.RefreshProfileFailAction(err)))
                );
            })
        )
    });

	// Nothing to show the user: the copy they already see simply stays as it was until the next attempt.
	refreshProfileFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.RefreshProfileFail),
            tap(() => {
                console.error(`FOUT: Profiel verversen mislukt.`);
            })
        );
    }, { dispatch: false });

	updateCoordinates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.UpdateCoordinates),
            switchMap((_: AuthActions.UpdateCoordinatesAction) => {
                const geolocationOptions = { timeout: 10000, enableHighAccuracy: true };
                // On native, Geolocation.getCurrentPosition() resolves through Capacitor's native
                // bridge, not through zone.js-patched browser APIs - same root cause already fixed
                // for Camera.getPhoto() in editteam.ts. Without re-entering the zone here, the
                // dispatched action (and every HTTP request gated on it via LocationInterceptor's
                // location$ subscription) runs outside Angular, so components update their data but
                // never get a render tick - stuck on a loading state until some unrelated click
                // forces a global tick.
                return new Observable<Action>(subscriber => {
                    Geolocation.getCurrentPosition(geolocationOptions).then(resp => {
                        this.zone.run(() => {
                            subscriber.next(new AuthActions.UpdateCoordinatesOkAction(resp.coords.latitude, resp.coords.longitude));
                            subscriber.complete();
                        });
                    }).catch(err => {
                        // err is a raw GeolocationPositionError (a DOM object, not a plain object) when
                        // this comes from a real browser rejection - dispatching it directly as an action
                        // payload trips NgRx's strictActionSerializability runtime check (dev only), which
                        // throws *inside* the store's reducer pipeline and permanently kills all further
                        // state updates for the rest of the session (Effects keep running since they're on
                        // a separate stream, but nothing the reducer produces is ever seen again - e.g. a
                        // real login afterwards looks like it "succeeds" against the backend but the UI
                        // stays on the anonymous session forever). Extract only plain, serializable fields.
                        this.zone.run(() => {
                            subscriber.next(new AuthActions.UpdateCoordinatesFailAction(
                                err ? { code: err.code, message: err.message } : null
                            ));
                            subscriber.complete();
                        });
                    });
                });
            })
        )
    });

	// @Effect({ dispatch: false})
	// updateCoordinatesOk$ = this.actions$.pipe(
	// 	ofType(AuthActionTypes.UpdateCoordinatesOk),
	// 	tap(() => { console.debug("Set coordinates OK!"); })
	// );

	updateCoordinatesFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActionTypes.UpdateCoordinatesFail),
            tap(error => {
                console.error(`FOUT: Update coordinates failed.`, error);
            })
        );
    } , { dispatch: false });
}

// A stored Pro Pool ID login or app-local anonymous session (ID-8).
function hasSession(store: any): boolean {
	return !!(store?.auth?.access_token || store?.auth?.anonymous_token);
}

// Action payloads must stay serializable (NgRx runtime checks): keep only the OAuth error code or message.
function loginError(error: any): { error: string } {
	return { error: error?.error ?? error?.message ?? String(error) };
}
